import { useEffect, useRef } from "react";

const INSET_PROPERTY = "--cmcp-bottom-overlay-inset";
const COMPOSER_INSET_PROPERTY = "--cmcp-composer-bottom-inset";
const RAIL_HEIGHT_PROPERTY = "--cmcp-live-rail-height";
type OverlayKind = "overlay" | "composer" | "rail";
const activeInsets = new Map<symbol, { kind: OverlayKind; inset: number; height: number; schedule: () => void }>();

function setInset(property: string, values: number[]) {
  const style = document.documentElement.style;
  const value = values.length ? `${Math.ceil(Math.max(0, ...values))}px` : "";
  if (style.getPropertyValue(property) === value) return false;
  if (value) style.setProperty(property, value);
  else style.removeProperty(property);
  return true;
}

function publishInset() {
  const entries = [...activeInsets.values()];
  const railResized = setInset(RAIL_HEIGHT_PROPERTY, entries.filter(({ kind }) => kind === "rail").map(({ height }) => height));
  const composerMoved = setInset(COMPOSER_INSET_PROPERTY, entries.filter(({ kind }) => kind === "composer").map(({ inset }) => inset));
  // Reserve the furthest extent, including panels positioned above the composer.
  setInset(INSET_PROPERTY, entries.map(({ inset }) => inset));
  if (composerMoved) {
    // A new bottom offset moves the rail without resizing it. Measure that move
    // after CSS updates; only composer changes notify, avoiding a feedback loop.
    for (const entry of entries) if (entry.kind !== "composer") entry.schedule();
  }
  // Composer height depends on rail height, never on its position above it.
  if (railResized) for (const entry of entries) if (entry.kind === "composer") entry.schedule();
}

/** Publish the space occupied by a fixed bottom panel for pages that opt in. */
export function useBottomOverlayInset<T extends HTMLElement>(kind: OverlayKind = "overlay") {
  const ref = useRef<T>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const key = Symbol("bottom-overlay");
    let disposed = false;
    let frame: number | null = null;
    const measure = () => {
      frame = null;
      if (disposed) return;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const visible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < viewportHeight;
      activeInsets.set(key, { kind, inset: visible ? Math.max(0, viewportHeight - Math.max(0, rect.top)) : 0, height: visible ? rect.height : 0, schedule });
      publishInset();
    };
    const schedule = () => {
      if (!disposed && frame === null) frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(element);
    window.addEventListener("resize", schedule);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    measure();
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
      activeInsets.delete(key);
      publishInset();
    };
  }, [kind]);
  return ref;
}
